import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Box } from "@material-ui/core";
import { DeleteOutline, Edit } from "@material-ui/icons";
import { useHistory } from "react-router-dom";
import {
  deleteFleetSource,
  getFleetSources,
  getDriveParties,
} from "../../../services/AssetFleetSources/fleetSourceApisService";
import { DisplayMessage } from "../../../services/helpers";
import { useStyles } from "../../../styles/styles";
import AddItemButton from "../../Common/Buttons/AddItemButton";
import Header from "../../Common/Headers/Header";
import DeleteConfirmationModal from "../../Common/Modals/DeleteConfirmationModal";
import TableComponent from "../../Common/Table/TableComponent";
import { sortTableStringsNotCaseSensitive } from "../../../utils/tableSorting";
import { NewDriveFleetSourceRoute } from "../../../constants/routePaths";
import { sortTableBooleans } from "./../../../utils/tableSorting";
import DrivePartiesDropdown from './../../Common/Dropdowns/DrivePartiesDropdown';

const FleetSourcesTable = ({ resources, refreshGrid }) => {
  const classes = useStyles();
  const history = useHistory();

  const [fleetSources, setFleetSources] = useState([]);
  const [allFleetSources, setAllFleetSources] = useState([]);
  const [driveParties, setDriveParties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedFleetSourceId, setSelectedFleetSourceId] = useState(null);
  const [selectedPartyId, setSelectedPartyId] = useState(0);

  const tableSettings = useMemo(
    () => ({
      actions: [
        rowData => ({
          icon: () => <Edit />,
          tooltip: rowData.isDeleted ? "" : resources["Edit"],
          onClick: () => {
            if (!rowData.isDeleted) {
              history.push({
                pathname: `/fleetsources/${rowData.id}`,
                state: { fleetSourceId: rowData.id },
              });
            }
          },
          disabled: rowData.isDeleted,
        }),
        rowData => ({
          icon: () => <DeleteOutline />,
          tooltip: rowData.isDeleted ? "" : resources["Delete"],
          onClick: () => {
            if (!rowData.isDeleted) {
              handleDelete(rowData.id);
            }
          },
          disabled: rowData.isDeleted,
        }),
      ],
      columns: [
        {
          title: resources["Description"],
          field: "description",
          cellStyle: { width: "33%" },
          headerStyle: { width: "33%" },
          customSort: (a, b) =>
            sortTableStringsNotCaseSensitive(a.description, b.description),
        },
        {
          title: resources["DriveParty"],
          field: "partyName",
          cellStyle: { width: "33%" },
          headerStyle: { width: "33%" },
          customSort: (a, b) =>
            sortTableStringsNotCaseSensitive(a.partyName, b.partyName),
        },
        {
          title: resources["IsDeleted"],
          field: "isDeleted",
          cellStyle: { width: "33%" },
          headerStyle: { width: "33%" },
          lookup: { true: resources["Yes"], false: resources["No"] },
          customSort: (a, b) => sortTableBooleans(a.isDeleted, b.isDeleted),
        },
      ],
    }),
    [resources, history]
  );

  const handleDelete = (id) => {
    setSelectedFleetSourceId(id);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setSelectedFleetSourceId(null);
  };

  const confirmDelete = async () => {
    if (!selectedFleetSourceId) return;

    const result = await deleteFleetSource(selectedFleetSourceId);
    if (result) {
      setFleetSources((prev) =>
        prev.map((fs) =>
          fs.id === selectedFleetSourceId ? { ...fs, isDeleted: true } : fs
        )
      );
      DisplayMessage(
        resources["DriveFleetSourceWasDeletedWithSuccess"],
        "success"
      );
    }
    setSelectedFleetSourceId(null);
    closeDeleteModal();
  };

  const fetchDriveParties = useCallback(async () => {
    const data = await getDriveParties();
    setDriveParties(data ?? []);
  }, []);

  const fetchFleetSources = useCallback(async () => {
    setLoading(true);
    const data = await getFleetSources();
    if (data) {
      const formatted = data
        .sort((a, b) => a.id - b.id)
        .map(
          ({
            id,
            outletId,
            description,
            externalIdentifier,
            party,
            isDeleted,
          }) => ({
            id,
            outletId,
            description,
            externalIdentifier,
            partyId: party?.id,
            partyName: party?.description,
            isDeleted,
          })
        );
      setAllFleetSources(formatted);
      setFleetSources(formatted);
    }
    setLoading(false);
  }, []);

  const handlePartyChange = (e) => {
    const partyId = parseInt(e.target.value);
    setSelectedPartyId(partyId);
  };

  const filteredFleetSources = useMemo(() => {
    if (selectedPartyId > 0) {
      return allFleetSources.filter((fs) => fs.partyId === selectedPartyId);
    }
    return allFleetSources;
  }, [selectedPartyId, allFleetSources]);

  useEffect(() => {
    fetchFleetSources();
    fetchDriveParties();
  }, [fetchFleetSources, fetchDriveParties, refreshGrid]);

  useEffect(() => {
    setFleetSources(filteredFleetSources);
  }, [filteredFleetSources]);

  return (
    <Box>
      <Box className="position-relative py-4">
        <Header title={resources["DriveFleetSources"]} loading={loading} />
        {!loading && (
          <>
            <Box className="d-flex form-group">
              <DrivePartiesDropdown
                resources={resources}
                driveParty={selectedPartyId}
                driveParties={driveParties}
                onDrivePartyChange={handlePartyChange}
                containerClass="col-3"
                addMarginBottom
                id="driveParties-dropdown-grid"
                filterFunction={(a) => a.isActive === true}
              />
              <AddItemButton
                title={resources["AddDriveFleetSource"]}
                linkPath={NewDriveFleetSourceRoute}
                state={{ partyId: selectedPartyId }}
                idName="new-fleetsources"
              />
            </Box>
            <TableComponent
              resources={resources}
              data={fleetSources}
              tableSettings={tableSettings}
            />
          </>
        )}
        <DeleteConfirmationModal
          onConfirm={confirmDelete}
          resources={resources}
          areYouSureMessage={
            resources["AreYouSureYouWantToDeleteDriveFleetSource"]
          }
          open={deleteModalOpen}
          onClose={closeDeleteModal}
          className={classes.modal}
        />
      </Box>
    </Box>
  );
};

export default FleetSourcesTable;
